import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Privacy Policy. Required by both app stores before a build can ship, and
 * linked from Google Play as https://partyresume.com/privacy.
 *
 * Written against what the code ACTUALLY collects rather than a template: the
 * profile fields in Profile.cs, the processors in Services/, the push tokens,
 * and the analytics pipeline. If a field is added to a profile or a new
 * processor is introduced, this page is part of that change.
 */
const EFFECTIVE_DATE = 'September 9, 2026';
const COMPANY_NAME = 'Portal Inc.';
const COMPANY_ADDRESS = 'Vancouver, British Columbia, Canada';
const CONTACT_EMAIL = 'legal@partyresume.com';

function Section({ number, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-mono text-lg md:text-xl font-bold uppercase tracking-wider text-black mb-4">
        {number}. {title}
      </h2>
      <div className="font-mono text-sm leading-relaxed text-gray-800 space-y-3">
        {children}
      </div>
    </section>
  );
}

function SubSection({ number, title, children }) {
  return (
    <div className="ml-4 mb-4">
      <h3 className="font-mono text-sm font-bold uppercase tracking-wide text-black mb-2">
        {number} {title}
      </h3>
      <div className="font-mono text-sm leading-relaxed text-gray-800 space-y-2">
        {children}
      </div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-2 border-black">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="font-mono text-lg font-bold uppercase tracking-wider text-black hover:opacity-70 transition-opacity"
          >
            Portal
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-xs uppercase tracking-wider text-gray-600 hover:text-black transition-colors"
          >
            Back
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-mono text-2xl md:text-3xl font-bold uppercase tracking-wider text-black mb-2">
          Privacy Policy
        </h1>
        <p className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-2">
          What We Collect And Why
        </p>
        <p className="font-mono text-sm text-gray-600 mb-12">
          Effective Date: {EFFECTIVE_DATE}
        </p>

        <div className="mb-12 p-6 border-2 border-black">
          <p className="font-mono text-sm leading-relaxed text-gray-800">
            This Privacy Policy explains how {COMPANY_NAME} (&quot;Portal,&quot; &quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;) collects, uses, shares, and protects personal information
            when you use our website and mobile applications (the &quot;Platform&quot;). It forms part of
            the Portal <Link to="/terms" className="underline">Terms and Conditions</Link>.
            We are based in British Columbia, Canada, and handle personal information in accordance
            with Canadian privacy law, including PIPEDA.
          </p>
        </div>

        <Section number={1} title="Information You Give Us">
          <SubSection number="1.1" title="Account Information">
            <p>To create an account we collect:</p>
            <List items={[
              'Your email address',
              'A password, which we store only as a one-way hash and never in readable form',
              'Your phone number, if you choose to provide one — this is optional and used for event notifications',
              'A Google or Apple account identifier, if you sign in with one of those services',
            ]} />
            <p>
              If you sign in with Apple and choose &quot;Hide My Email,&quot; we receive a private relay
              address rather than your real one. That address works everywhere a normal one does, and
              we never learn the address behind it.
            </p>
          </SubSection>

          <SubSection number="1.2" title="Your Profile">
            <p>Your profile is what other people see. It may include:</p>
            <List items={[
              'Display name, handle, and profile photo',
              'Photos you add to your gallery',
              'The city or area you are in',
              'Pronouns, and whether you want them shown',
              'Your answer to the vibe check ("what do you bring to the party?")',
              'A social link',
            ]} />
          </SubSection>

          <SubSection number="1.3" title="Optional Sensitive Information">
            <p>
              Some profile questions touch on sensitive subjects. Every one of them is optional, every
              one offers &quot;prefer not to say,&quot; and leaving them blank does not limit your use of
              the Platform:
            </p>
            <List items={[
              'Gender',
              'Whether you drink alcohol',
              'Whether you use substances',
            ]} />
            <p>
              These exist so hosts and guests can find compatible company. We do not use them for
              advertising, we do not sell them, and we do not share them with third parties.
            </p>
          </SubSection>

          <SubSection number="1.4" title="Events, Bookings, and Messages">
            <p>
              When you host or attend events we collect the details you enter — titles, descriptions,
              dates, venues, posters, ticket tiers, and guest lists. When you message someone we store
              the message, including any voice notes, images, or links you send, so it can be delivered
              and shown in your conversation history. Chat on Portal is one-to-one only.
            </p>
          </SubSection>
        </Section>

        <Section number={2} title="Information We Collect Automatically">
          <SubSection number="2.1" title="Location">
            <p>
              With your permission, we use your approximate location to show what is happening near you.
              You can instead pick a city by hand, and you can turn location off in your device settings
              at any time. Events carry the coordinates of their venue so they can appear on maps and in
              nearby searches.
            </p>
            <p>
              Hosts may enable an approximate-location setting for an event, which deliberately offsets
              the pin until a guest has a ticket. This protects hosts of private gatherings; it is a
              display choice and not a security guarantee.
            </p>
          </SubSection>

          <SubSection number="2.2" title="Usage and Device Data">
            <List items={[
              'Pages and screens you view, and features you use',
              'IP address, browser, operating system, and device type',
              'Approximate geographic region derived from IP address',
              'Error and performance logs',
              'Device push tokens, if you enable notifications',
            ]} />
            <p>
              We use this to understand which parts of the Platform are useful, to fix faults, and to
              detect abuse. We do not use third-party advertising trackers.
            </p>
          </SubSection>

          <SubSection number="2.3" title="Cookies and Local Storage">
            <p>
              We use cookies and browser storage to keep you signed in, remember preferences such as your
              chosen city and theme, and keep the app working offline where possible. We do not use
              advertising cookies. Blocking them will sign you out and may break parts of the Platform.
            </p>
          </SubSection>
        </Section>

        <Section number={3} title="Payments">
          <p>
            Card payments are processed by Stripe. Card numbers are entered directly into Stripe and are
            never sent to or stored on our servers. We keep a record of the transaction — amounts, dates,
            what it was for, and the last digits and card brand Stripe reports back — so we can show your
            history, issue refunds, pay hosts, and meet our tax obligations.
          </p>
          <p>
            Your Portal wallet is a ledger of what we owe you. It records top-ups, ticket purchases,
            refunds, and payouts. Hosts who receive payouts complete onboarding directly with Stripe, which
            may require identity information that Stripe collects and holds under its own privacy policy.
          </p>
        </Section>

        <Section number={4} title="How We Use Information">
          <List items={[
            'To create and run your account and profile',
            'To show you events, people, and places relevant to where you are',
            'To process tickets, payments, refunds, and host payouts',
            'To deliver messages, notifications, and email you have asked for',
            'To keep the Platform safe — detecting fraud, abuse, and misuse',
            'To fix problems and improve how the Platform works',
            'To meet legal, tax, and accounting obligations',
          ]} />
          <p>
            We do not sell your personal information. We do not share it with advertisers. We do not use
            your messages to build advertising profiles.
          </p>
        </Section>

        <Section number={5} title="What Other People Can See">
          <p>
            Your profile, including your display name, photo, handle, and anything you choose to show, is
            visible to other users. Hosts of events you buy a ticket for can see that you are attending and
            the name on your account. Guests who choose to appear on a guest list are visible to others
            attending that event.
          </p>
          <p>
            Event locations are revealed only to people holding a ticket. Read receipts do not exist on
            Portal — we never tell the other person whether you have read a message.
          </p>
        </Section>

        <Section number={6} title="Service Providers">
          <p>
            We share the minimum necessary with companies that operate parts of the Platform on our behalf.
            Each is bound to use it only for that purpose:
          </p>
          <List items={[
            'Stripe — payment processing and host payouts',
            'Brevo — transactional email such as receipts, confirmations, and password resets',
            'DigitalOcean — hosting, databases, and storage of uploaded images',
            'Apple and Google — sign-in, and delivery of push notifications to your device',
            'Mapbox — maps and place search',
            'Tenor — GIF search in messages (your search terms reach Tenor; your identity does not)',
            'Cloudflare — video hosting and delivery',
          ]} />
          <p>
            Some of these providers operate outside Canada, including in the United States. Information
            handled there may be accessible to authorities under the laws of those countries.
          </p>
        </Section>

        <Section number={7} title="Publicly Listed Events">
          <p>
            Some events on Portal are collected from publicly available sources such as venue
            websites and public listings, so you can find what is happening in your area. That information
            concerns businesses and public events, not private individuals. If you are an organizer and
            want a listing removed, contact us and we will remove it.
          </p>
        </Section>

        <Section number={8} title="Keeping Information Safe">
          <p>
            Connections use encryption in transit. Passwords are stored only as one-way hashes. Access to
            production systems is limited to those who need it. Card details never reach our servers.
          </p>
          <p>
            No system is perfectly secure, and we will not claim otherwise. If a breach affects your
            personal information, we will notify you and the relevant authorities as the law requires.
          </p>
        </Section>

        <Section number={9} title="How Long We Keep It">
          <p>
            We keep your account information for as long as your account exists. Records of transactions
            are kept for as long as tax and accounting law requires, which in Canada is generally seven
            years, even after an account is closed. Messages remain until deleted by a participant or with
            the account. Analytics data is retained in aggregate form.
          </p>
        </Section>

        <Section number={10} title="Your Rights and Choices">
          <p>Under Canadian privacy law you may:</p>
          <List items={[
            'Ask what personal information we hold about you and request a copy',
            'Correct information that is wrong or incomplete',
            'Delete your account and the personal information attached to it',
            'Withdraw consent for optional collection, such as location or notifications',
            'Complain to the Office of the Privacy Commissioner of Canada',
          ]} />
          <p>
            You can change or remove most profile information yourself at any time, including the optional
            sensitive fields. To delete your account, use the account settings in the app, or email us at
            the address below and we will action it.
          </p>
          <p>
            Some information survives deletion where the law requires it — principally transaction records
            kept for tax purposes. Where that happens we keep only what is required and nothing more.
          </p>
        </Section>

        <Section number={11} title="Notifications and Email">
          <p>
            You choose whether to receive push notifications, and you can turn them off in your device
            settings or in the app. Transactional email — tickets, receipts, password resets, and security
            notices — is part of the service and cannot be turned off while you have an account. Marketing
            email is opt-in and every message includes an unsubscribe link.
          </p>
        </Section>

        <Section number={12} title="Children">
          <p>
            Portal is not intended for anyone under the age of majority in their province, state, or
            country, and we do not knowingly collect information from them. If you believe a child has
            given us personal information, contact us and we will delete it.
          </p>
        </Section>

        <Section number={13} title="Changes to This Policy">
          <p>
            We may update this policy. If a change materially affects how we handle your personal
            information we will tell you in the app or by email before it takes effect. The effective date
            at the top always reflects the current version.
          </p>
        </Section>

        <Section number={14} title="Contact Us">
          <p>For any privacy question, or to exercise any of the rights above:</p>
          <div className="mt-3 p-4 border-2 border-black">
            <p className="font-bold">{COMPANY_NAME}</p>
            <p>{COMPANY_ADDRESS}</p>
            <p>{CONTACT_EMAIL}</p>
          </div>
        </Section>

        <div className="mt-16 pt-8 border-t-2 border-black">
          <p className="font-mono text-xs text-gray-500 text-center uppercase tracking-wider">
            Portal &mdash; Find Your Party. Find Your People.
          </p>
        </div>
      </main>
    </div>
  );
}
